import type { ReactNode } from 'react';
import { ArrowLeft, X, type LucideIcon } from 'lucide-react';
import { v2 } from '@/lib/i18n/v2';
import { useHapticTarget } from '@/contexts/HapticsContext';
import styles from './page-header.module.less';

export interface BackButtonProps {
  onClick: () => void;
  /** Icon variant - ArrowLeft or X (close) */
  variant?: 'arrow' | 'close';
  /** Use white background (for colored pages) */
  white?: boolean;
}

export function BackButton({ onClick, variant = 'arrow', white }: BackButtonProps) {
  const Icon: LucideIcon = variant === 'close' ? X : ArrowLeft;
  const hapticRef = useHapticTarget<HTMLButtonElement>();

  return (
    <button
      ref={hapticRef}
      className={`${styles.backButton} ${white ? styles.backButtonWhite : ''}`}
      onClick={onClick}
      aria-label={variant === 'close' ? v2.common.close : v2.common.goBack}
    >
      <Icon size={variant === 'close' ? 20 : 24} />
    </button>
  );
}

export interface ActionButtonProps {
  onClick: () => void;
  /** Icon component */
  icon: LucideIcon;
  /** Button variant - 'secondary' (gray) or 'primary' (dark) */
  variant?: 'secondary' | 'primary';
  /** Active state - shows green for voted/liked */
  active?: boolean;
  /** Dislike state - shows pink/red for disliked */
  dislike?: boolean;
  reaction?: ReactNode;
  disabled?: boolean;
  'aria-label': string;
}

export function ActionButton({
  onClick,
  icon: Icon,
  variant = 'secondary',
  active = false,
  dislike = false,
  reaction,
  disabled,
  ...props
}: ActionButtonProps) {
  const hapticRef = useHapticTarget<HTMLButtonElement>();
  return (
    <button
      ref={hapticRef}
      className={`${styles.actionButton} ${variant === 'primary' ? styles.actionButtonPrimary : ''} ${active ? styles.actionButtonActive : ''} ${dislike ? styles.actionButtonDislike : ''} ${reaction ? styles.actionButtonReacting : ''}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {!reaction && <Icon size={20} />}
      {reaction}
    </button>
  );
}
